// The table's membership as the wire carries it (slice 03, issue 743). Roles
// and seats are the site's, read live; what is here is the projection every
// member sees: names, roles and colours — shared facts by design
// (design/13-ownership-and-roles.md §6), so every member receives the same
// people. Not the same parties (issue 1579): which party a person or a
// character is in, and a character of a party they do not sit in, reaches a
// player only as `table/parties.ts`'s `membersFor` projects it.
export const TABLE_JOIN = 'table:join';
export const TABLE_MEMBERS = 'table:members';
export const TABLE_REMOVED = 'table:removed';
export const TABLE_OPENED = 'table:opened';
/**
 * Server → sockets (qa-runs 1429): the DM's Session Active? check. To a socket waiting outside the table,
 * `{ campaignId, active }` (on: join again; off: wait); to the DM's and co-DMs' sockets also `waiting`, how many people
 * wait. A player seated when it goes off is told `active: false` and is no longer at the table.
 */
export const TABLE_SESSION = 'table:session';
/** This person's role at a table changed while connected (a handover): a fresh snapshot as the new role, counter reset. */
export const TABLE_ROLE = 'table:role';
/** Server → room: the scene changed (the map was set or cleared); a client re-reads the table. */
export const TABLE_SCENE = 'table:scene';
/** DM → server (issue 834): look at another scene of the table; the reply is its snapshot, the counter reset. */
export const TABLE_VIEW_SCENE = 'table:view-scene';
/** Server → a player (issue 834): the DM put another scene live; a fresh snapshot of it, the counter reset. */
export const TABLE_SWITCH = 'table:switch';
/**
 * Server → a DM socket NOT on the live scene (issue 840): something changed on the live scene. Content-free — the
 * campaign id and nothing else — so a DM staging elsewhere knows to re-read "view as a player". Never sent to a player.
 */
export const TABLE_LIVE_CHANGED = 'table:live-changed';
/**
 * Server → one person's sockets at a table: `{ campaignId, text }`, a notice for them alone ("The DM gave you a
 * Longsword."), shown as any notice is. Never written to the log and never in a snapshot. It may carry `offer`, a
 * failed combat maneuver's try-back (qa-runs#398, `ManeuverOffer`): a button that sends that maneuver the other way.
 */
export const TABLE_NOTICE = 'table:notice';
/**
 * Server → a linked token's owners and whoever runs the table (qa-runs 1258): `{ campaignId, token }`, its character's
 * sheet changed. Content-free: their hot bar and pickers read what they read again.
 */
export const TABLE_SHEET = 'table:sheet';
/**
 * Server → the sockets of the table's DM and its co-GMs with `tokens` (qa-runs 1197): a held hoard was held, renamed,
 * given from or discarded. Content-free — `{ campaignId }` and nothing else — so their Loot tool reads its held list
 * again. Never sent to a player or a watcher: held hoards are the DM's alone (qa-runs 1156).
 */
export const TABLE_HOARDS = 'table:hoards';
/**
 * A quick ping (issue 1039, COMM-003; Tom's choice A, hold to ping a spot). Client → server: `{ campaignId, x, y }`, a
 * point on the board; server → every socket on the sender's scene: a `Ping`. A moment's pointing, so never written to
 * the table's log and never in a snapshot; a staged scene's ping reaches nobody else, as its changes do.
 */
export const TABLE_PING = 'table:ping';
/**
 * Whether this table is open and in front of the person on this connection (issue 1182): client → server
 * `{ campaignId, focused }`, sent when the page's visibility or focus changes. Nothing is answered or broadcast; the server
 * only uses it to send no push notification to someone already looking at that table.
 */
export const TABLE_FOCUS = 'table:focus';
/** At most one ping a socket in `everyMs`; a point farther than `coordinate` from the origin is not on any board. */
export const PING_LIMITS = { everyMs: 400, coordinate: 100_000 };
export const PING_MS = 1600;
/** The point a ping request names, or null. */
export function parsePingRequest(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const b = v;
    const ok = (n) => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= PING_LIMITS.coordinate;
    if (typeof b.campaignId !== 'string' || !ok(b.x) || !ok(b.y))
        return null;
    return { campaignId: b.campaignId, x: Math.round(b.x * 10) / 10, y: Math.round(b.y * 10) / 10 };
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_RE = /^#[0-9a-f]{6}$/;
function parseMember(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const m = value;
    if (typeof m.id !== 'string' || !UUID_RE.test(m.id))
        return null;
    if (m.handle !== null && typeof m.handle !== 'string')
        return null;
    if (m.role !== 'dm' && m.role !== 'player' && m.role !== 'watcher' && m.role !== 'codm')
        return null;
    if (m.colour !== null && (typeof m.colour !== 'string' || !HEX_RE.test(m.colour)))
        return null;
    // Issue 1023: an older server sends neither; a malformed character is dropped, never the member.
    const characters = Array.isArray(m.characters)
        ? m.characters.flatMap((c) => {
            const { id, name, parties } = (c ?? {});
            if (typeof id !== 'string' || !UUID_RE.test(id) || typeof name !== 'string')
                return [];
            const tags = parsePartyTags(parties);
            return [tags.length ? { id, name, parties: tags } : { id, name }];
        })
        : [];
    const parties = parsePartyTags(m.parties);
    return {
        id: m.id,
        handle: m.handle,
        displayName: typeof m.displayName === 'string' && m.displayName.trim() ? m.displayName : null,
        characters,
        role: m.role,
        colour: m.colour,
        colourLocked: m.colourLocked === true,
        present: m.present === true,
        ...(m.guest === true ? { guest: true } : {}),
        ...(parties.length ? { parties } : {}),
    };
}
/** The parties a list names (issue 1579), each well formed and once; anything else is dropped, never the list. */
export function parsePartyTags(value) {
    if (!Array.isArray(value))
        return [];
    const seen = new Set();
    return value.flatMap((p) => {
        const { id, name } = (p ?? {});
        if (typeof id !== 'string' || !UUID_RE.test(id) || typeof name !== 'string' || seen.has(id))
            return [];
        seen.add(id);
        return [{ id, name }];
    });
}
/** The members list as broadcast; anything malformed reads as null. */
export function parseMembers(value) {
    if (!Array.isArray(value))
        return null;
    const out = [];
    for (const v of value) {
        const m = parseMember(v);
        if (!m)
            return null;
        out.push(m);
    }
    return out;
}
export function parseTableRemoved(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const { campaignId, name } = value;
    if (typeof campaignId !== 'string' || !UUID_RE.test(campaignId))
        return null;
    if (typeof name !== 'string')
        return null;
    const gone = value.gone === true;
    return gone ? { campaignId, name, gone } : { campaignId, name };
}
//# sourceMappingURL=table.js.map